import { type FormEvent, type ReactNode, useMemo, useState } from "react";
import type { DayMetrics, MetricsRange, PortalApi } from "../api.ts";
import { durationText } from "../duration.ts";
import { Id } from "../Id.tsx";
import { LocalTime } from "../LocalTime.tsx";
import { useLoad } from "../load.ts";
import {
  COMPLETENESS_TARGET,
  dayCounts,
  defaultRange,
  percentText,
  rangeProblem,
} from "../metrics.ts";
import { localTimeZone } from "../time.ts";
import { Button, Field, Input, LinkButton, Notice, PageLayout, Table, Toolbar } from "../ui.tsx";

/**
 * The v0 metrics over a range of the viewer's local days (docs/capture.md#completeness):
 * completeness against the 95% target with its three gaps, daily use, interventions per run and
 * time per run. Each day is from local midnight to the next in the browser's time zone, which the
 * page names and sends; the data API cuts the daily job's per-segment results into those days,
 * and what the job has not settled yet is pending. Every time shows in local time.
 */
export function MetricsPage({ api, runLink }: { api: PortalApi; runLink?: RunLink }) {
  const [range, setRange] = useState(() => defaultRange());
  const [draft, setDraft] = useState(range);
  const [selected, setSelected] = useState<string>();
  const timeZone = useMemo(() => localTimeZone(), []);
  const problem = rangeProblem(draft.from, draft.to);
  const metrics = useLoad(
    () => api.metrics(range.from, range.to, timeZone),
    [api, range, timeZone],
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (problem) return;
    setSelected(undefined);
    setRange(draft);
  };

  const data = metrics.data;
  const day = data?.days.find((entry) => entry.date === selected);

  return (
    <PageLayout title="Metrics">
      <Toolbar onSubmit={submit}>
        <Field label="From">
          <Input
            type="date"
            value={draft.from}
            onChange={(event) => setDraft({ ...draft, from: event.target.value })}
          />
        </Field>
        <Field label="To">
          <Input
            type="date"
            value={draft.to}
            onChange={(event) => setDraft({ ...draft, to: event.target.value })}
          />
        </Field>
        <Button type="submit" disabled={problem !== undefined}>
          Show
        </Button>
        {problem && <span className="text-bad text-ui">{problem}</span>}
        <span className="text-muted text-ui">days from local midnight ({timeZone})</span>
      </Toolbar>

      {metrics.loading && <p className="text-muted">Loading…</p>}
      {metrics.error && <Notice tone="bad">{metrics.error}</Notice>}

      {!metrics.loading && !metrics.error && data && (
        <>
          <Summary metrics={data} />
          <JobNote metrics={data} />
          <Table>
            <thead>
              <tr>
                <th>Day</th>
                <th>Completeness</th>
                <th>Complete / units</th>
                <th>No recording</th>
                <th>No transcript</th>
                <th>Run without segment</th>
                <th>Pending · open</th>
                <th>Producers</th>
                <th>Sessions</th>
                <th>Interventions / run</th>
                <th>Runs ended</th>
                <th>Median elapsed · active</th>
              </tr>
            </thead>
            <tbody>
              {data.days.map((entry) => (
                <DayRow
                  key={entry.date}
                  day={entry}
                  selected={entry.date === selected}
                  onSelect={() => setSelected(entry.date === selected ? undefined : entry.date)}
                />
              ))}
            </tbody>
          </Table>

          {day && <DayDetail day={day} runLink={runLink} />}
        </>
      )}
    </PageLayout>
  );
}

/** A link to a run in the run browser, for operators; absent for admins. */
export type RunLink = (runId: string) => string;

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col rounded-lg border border-line bg-raised px-4 pt-3.5 pb-4 [&>small]:text-muted [&>small]:text-xs/[1.55] [&>span]:mb-2 [&>span]:block [&>span]:font-semibold [&>span]:text-[1.375rem]/tight [&>span]:tabular-nums [&>span]:tracking-tight md:[&>span]:text-[1.75rem]/tight">
      <h2 className="mt-0 mb-1.5 font-medium text-muted text-xs tracking-normal">{title}</h2>
      {children}
    </div>
  );
}

/** The range's cards: the data API's total, the range taken as one window. */
function Summary({ metrics }: { metrics: MetricsRange }) {
  const { completeness, dailyUse, interventions, timing } = metrics.total;
  const { daysUnderTarget, daysInUse } = dayCounts(metrics.days);
  const meetsTarget =
    completeness.percent === undefined ? undefined : completeness.percent >= COMPLETENESS_TARGET;
  return (
    <div className="mt-5 mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <Card title="Completeness">
        <span className={meetsTarget === undefined ? "" : meetsTarget ? "text-ok" : "text-bad"}>
          {percentText(completeness.percent)}
        </span>
        <small>
          target {COMPLETENESS_TARGET}% ·{" "}
          {meetsTarget === undefined ? "nothing to measure" : meetsTarget ? "met" : "below target"}
          <br />
          {completeness.complete} of {completeness.units} complete · {daysUnderTarget} days under
          target
        </small>
      </Card>
      <Card title="Daily use">
        <span>{daysInUse}</span>
        <small>
          days with a producer active, of {metrics.days.length}
          <br />
          {dailyUse.producers} producers in the range
        </small>
      </Card>
      <Card title="Interventions per run">
        <span>{interventions.perRun ?? "—"}</span>
        <small>
          {interventions.messages} messages over {interventions.runs} runs measured
        </small>
      </Card>
      <Card title="Time per run">
        <span>{durationText(timing.meanElapsedSeconds)}</span>
        <small>
          mean elapsed of {timing.runs} runs ended
          <br />
          {durationText(timing.meanActiveSeconds)} mean active
        </small>
      </Card>
    </div>
  );
}

/** When the job last settled what it could, and so what is still pending. */
function JobNote({ metrics }: { metrics: MetricsRange }) {
  if (metrics.evaluatedAt === undefined || metrics.cutoff === undefined) {
    return (
      <Notice tone="info">
        The daily job has not run yet: it runs at 03:00 in Los Angeles, and until then every segment
        is pending.
      </Notice>
    );
  }
  return (
    <Notice tone="info">
      The daily job last ran <LocalTime iso={metrics.evaluatedAt} />: segments that ended after{" "}
      <LocalTime iso={metrics.cutoff} /> are pending until its next run, at 03:00 in Los Angeles.
    </Notice>
  );
}

function DayRow({
  day,
  selected,
  onSelect,
}: {
  day: DayMetrics;
  selected: boolean;
  onSelect: () => void;
}) {
  const { completeness: c, dailyUse, interventions, timing } = day;
  const under = c.percent !== undefined && c.percent < COMPLETENESS_TARGET;
  return (
    <tr className={selected ? "bg-selected" : undefined}>
      <td>
        <LinkButton type="button" onClick={onSelect} aria-pressed={selected}>
          {day.date}
        </LinkButton>
        {day.truncated && (
          <span className="text-bad" title="An index read hit its bound; some counts may be low">
            {" "}
            (truncated)
          </span>
        )}
      </td>
      <td className={under ? "text-bad" : c.percent === undefined ? "" : "text-ok"}>
        {percentText(c.percent)}
      </td>
      <td>
        {c.complete} / {c.units}
      </td>
      <td>{c.withoutRecording.count}</td>
      <td>{c.withoutSession.count}</td>
      <td>{c.runsWithoutSegment.count}</td>
      <td>
        {c.pending} · {c.open}
      </td>
      <td>{dailyUse.producers}</td>
      <td>{dailyUse.sessions}</td>
      <td>{interventions.perRun ?? "—"}</td>
      <td>{timing.runs}</td>
      <td>
        {timing.runs
          ? `${durationText(timing.medianElapsedSeconds)} · ${durationText(timing.medianActiveSeconds)}`
          : "—"}
      </td>
    </tr>
  );
}

function DayDetail({ day, runLink }: { day: DayMetrics; runLink?: RunLink | undefined }) {
  const { completeness: c, dailyUse, interventions } = day;
  const run = (runId: string) => <Id id={runId} href={runLink?.(runId)} />;
  const more = (omitted: number) =>
    omitted > 0 ? <p className="text-muted">and {omitted} more</p> : null;
  return (
    <div className="mt-10 border-line border-t pt-1">
      <h2 className="mt-6 font-bold text-[1.375rem]">{day.date}</h2>
      <p className="text-muted">
        From <LocalTime iso={day.start} /> to <LocalTime iso={day.end} />. {c.pending} segments are
        pending and {c.open} open; {c.invalidRecordings} recordings arrived but could not be read.
      </p>

      <h3>Gaps</h3>
      <h4>A recording never arrived ({c.withoutRecording.count})</h4>
      <SegmentGapList gaps={c.withoutRecording.segments} run={run} />
      {more(c.withoutRecording.omitted)}
      <h4>No transcript was joined ({c.withoutSession.count})</h4>
      <SegmentGapList gaps={c.withoutSession.segments} run={run} />
      {more(c.withoutSession.omitted)}
      <h4>A run was named but never recorded ({c.runsWithoutSegment.count})</h4>
      {c.runsWithoutSegment.runs.length > 0 && (
        <Table compact>
          <thead>
            <tr>
              <th>Run</th>
              <th>First named</th>
              <th>Named by sessions</th>
            </tr>
          </thead>
          <tbody>
            {c.runsWithoutSegment.runs.map((gap) => (
              <tr key={gap.runId}>
                <td>{run(gap.runId)}</td>
                <td>
                  <LocalTime iso={gap.firstNamedAt} />
                </td>
                <td>
                  {gap.sessionIds.map((sessionId, i) => (
                    <span key={sessionId}>
                      {i > 0 && ", "}
                      <Id id={sessionId} />
                    </span>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {more(c.runsWithoutSegment.omitted)}

      <h3>Daily use</h3>
      <p>
        {dailyUse.producers} producers, {dailyUse.sessions} sessions.
      </p>
      {dailyUse.byProducer.length > 0 && (
        <Table compact>
          <thead>
            <tr>
              <th>Producer</th>
              <th>Sessions</th>
            </tr>
          </thead>
          <tbody>
            {dailyUse.byProducer.map((use) => (
              <tr key={use.producerId}>
                <td>{use.email ?? <code>{use.producerId}</code>}</td>
                <td>{use.sessions}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {more(dailyUse.omitted)}

      <h3>Interventions per run</h3>
      <p>
        {interventions.messages} messages over {interventions.runs} runs:{" "}
        {interventions.perRun ?? "—"} per run. {interventions.unmeasuredSegments} segments had no
        transcript to count in.
      </p>
      {interventions.byRun.length > 0 && (
        <Table compact>
          <thead>
            <tr>
              <th>Run</th>
              <th>Segments</th>
              <th>Messages</th>
            </tr>
          </thead>
          <tbody>
            {interventions.byRun.map((entry) => (
              <tr key={entry.runId}>
                <td>{run(entry.runId)}</td>
                <td>{entry.segments}</td>
                <td>{entry.messages}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {more(interventions.omitted)}

      <h3>Time per run</h3>
      <RunTimes timing={day.timing} run={run} more={more} />
    </div>
  );
}

function RunTimes({
  timing,
  run,
  more,
}: {
  timing: DayMetrics["timing"];
  run: (runId: string) => ReactNode;
  more: (omitted: number) => ReactNode;
}) {
  const pending =
    timing.pending > 0 ? (
      <p className="text-muted">{timing.pending} more ended and are not timed yet.</p>
    ) : null;
  if (timing.runs === 0) {
    return (
      <>
        <p>No run ended that day{timing.pending > 0 ? " that the job has timed" : ""}.</p>
        {pending}
      </>
    );
  }
  return (
    <>
      <p>
        {timing.runs} runs ended. Elapsed: median {durationText(timing.medianElapsedSeconds)}, mean{" "}
        {durationText(timing.meanElapsedSeconds)}. Active: median{" "}
        {durationText(timing.medianActiveSeconds)}, mean {durationText(timing.meanActiveSeconds)}.
      </p>
      {pending}
      <Table>
        <thead>
          <tr>
            <th>Run</th>
            <th>Carrier</th>
            <th>Status</th>
            <th>Started</th>
            <th>Ended</th>
            <th>Elapsed</th>
            <th>Active</th>
            <th>Segments</th>
          </tr>
        </thead>
        <tbody>
          {timing.byRun.map((entry) => (
            <tr key={entry.runId}>
              <td>{run(entry.runId)}</td>
              <td>{entry.carrier ?? "—"}</td>
              <td>{entry.status}</td>
              <td>
                <LocalTime iso={entry.startedAt} />
              </td>
              <td>
                <LocalTime iso={entry.endedAt} />
              </td>
              <td>{durationText(entry.elapsedSeconds)}</td>
              <td>{durationText(entry.activeSeconds)}</td>
              <td>
                {entry.segments}
                {entry.openSegments > 0 && (
                  <span className="text-muted"> ({entry.openSegments} open)</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {more(timing.omitted)}
    </>
  );
}

function SegmentGapList({
  gaps,
  run,
}: {
  gaps: DayMetrics["completeness"]["withoutRecording"]["segments"];
  run: (runId: string) => ReactNode;
}) {
  if (gaps.length === 0) return null;
  return (
    <Table>
      <thead>
        <tr>
          <th>Run</th>
          <th>Segment</th>
          <th>Producer</th>
          <th>Ended</th>
        </tr>
      </thead>
      <tbody>
        {gaps.map((gap) => (
          <tr key={`${gap.runId}/${gap.segmentId}`}>
            <td>{run(gap.runId)}</td>
            <td>
              <Id id={gap.segmentId} />
            </td>
            <td>
              <code>{gap.producerId ?? "—"}</code>
            </td>
            <td>
              <LocalTime iso={gap.endedAt} />
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
